// Lesson order in the admin course editor (plan R1 §1.1 A1/A2/A4/A8, §1.2 T2/T5).
//
// Everything here is addressed by lesson uuid against the REAL lesson array of a
// section — never by the index of a sorted, filtered or paged table row — and
// returns new arrays/objects (the course in React state is never mutated). The
// server does the real reorder (PUT /v1/courses/admin/:id/lessons/order) from
// its stored course; these helpers only build that request and the optimistic
// screen state that is rolled back if it fails.
import { Course, CourseLessonRepairs, Item, Section } from 'src/@types/Course';

export const CLEAR_SEARCH_TO_REORDER_TEXT = 'Clear the search to change the order';

/** The uuids of a section's lessons in their stored order (empty slots skipped). */
export function lessonUuids(section: Section | null | undefined): string[] {
  return (section?.items ?? [])
    .map((item) => (item && typeof item.uuid === 'string' ? item.uuid : ''))
    .filter(Boolean);
}

/**
 * The list with `uuid` moved by `delta` (-1 = up, +1 = down). `null` when the
 * move is a no-op (first up, last down), the uuid is unknown, or the list holds
 * the uuid more than once — the caller then sends nothing.
 */
export function moveUuid(list: string[], uuid: string, delta: number): string[] | null {
  const from = list.indexOf(uuid);
  if (from < 0 || list.indexOf(uuid, from + 1) >= 0) return null;
  const to = from + delta;
  if (!Number.isInteger(delta) || delta === 0 || to < 0 || to >= list.length) return null;
  const next = list.slice();
  next.splice(from, 1);
  next.splice(to, 0, uuid);
  return next;
}

/**
 * A copy of `course` with section `sectionIndex`'s lessons in the order of
 * `uuids` — the optimistic screen state of a move. `null` unless `uuids` is
 * exactly that section's lessons (same rule as the server's set check), so a
 * stale list can never drop, duplicate or invent a lesson on screen.
 */
export function withSectionOrder(
  course: Course,
  sectionIndex: number,
  uuids: string[]
): Course | null {
  const section = course.sections?.[sectionIndex];
  if (!section) return null;
  const items = (section.items ?? []).filter(Boolean) as Item[];
  const current = lessonUuids(section);
  if (current.length !== items.length || current.length !== uuids.length) return null;
  if (new Set(current).size !== current.length) return null;
  const byUuid = new Map(items.map((item) => [item.uuid as string, item]));
  const ordered: Item[] = [];
  for (const uuid of uuids) {
    const item = byUuid.get(uuid);
    if (!item) return null;
    byUuid.delete(uuid);
    ordered.push(item);
  }
  const sections = course.sections.map((s, i) =>
    i === sectionIndex ? { ...s, items: ordered } : s
  );
  return { ...course, sections };
}

/** Position of the lesson in the section's REAL array (-1 when absent). */
export function realIndexOf(items: Item[] | undefined, uuid: string | undefined): number {
  if (!uuid) return -1;
  return (items ?? []).findIndex((item) => item?.uuid === uuid);
}

/** First / last in the REAL lesson order, whatever page or filter shows the row. */
export function edgeFlags(
  items: Item[] | undefined,
  uuid: string | undefined
): { isFirst: boolean; isLast: boolean } {
  const list = (items ?? []).filter(Boolean);
  const index = list.findIndex((item) => item?.uuid === uuid);
  return { isFirst: index <= 0, isLast: index < 0 || index === list.length - 1 };
}

/**
 * Whether the ↑/↓ arrows may be used: never while a title search hides some
 * rows (a move in a filtered view would jump over hidden lessons), while a save
 * runs, on a read-only (partner) course, or while lesson problems wait for a fix.
 */
export function canReorder({
  filterName = '',
  saving = false,
  readOnly = false,
  locked = false,
}: {
  filterName?: string;
  saving?: boolean;
  readOnly?: boolean;
  locked?: boolean;
}): boolean {
  return !filterName.trim() && !saving && !readOnly && !locked;
}

/** React key of a lesson row: its uuid, or its real position for an id-less row. */
export function rowKey(row: Item | null | undefined, realIndex: number): string {
  return row?.uuid || `slot-${realIndex}`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "1 empty lesson slot, 2 lessons without an id" — the repairs banner text. */
export function repairsText(repairs: CourseLessonRepairs | undefined | null): string {
  if (!repairs) return '';
  const parts: string[] = [];
  if (repairs.emptySlots)
    parts.push(plural(repairs.emptySlots, 'empty lesson slot', 'empty lesson slots'));
  if (repairs.idsMissing)
    parts.push(plural(repairs.idsMissing, 'lesson without an id', 'lessons without an id'));
  if (repairs.duplicateIds) {
    parts.push(plural(repairs.duplicateIds, 'repeated lesson id', 'repeated lesson ids'));
  }
  return parts.join(', ');
}
